import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  Fragment,
} from 'react'
import { navigate } from 'gatsby'
import nanoid from 'nanoid'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import { TabPanel as TabPanelContainer } from 'react-tabs'
import useMediaQuery from 'react-hook-media-query'

import { mediaqueries } from '~/utils/mediaqueries'
import fbTrack from '~/utils/fb'
import StoreContext from '~/context/StoreContext'
import DesignContext from '~/context/DesignContext'
import ConstantsContext from '~/context/ConstantsContext'

import AddToCart from '~/components/AddToCart'
import SEO from '~/components/seo'
import Collapsable from '~/components/Collapsable'
import ConditionalWrapper from '~/components/ConditionalWrapper'
import Picker, { Scale, PersonalisedText, Invert } from '~/components/Picker'
import { GreatPrimer } from '~/components/Typography'
import { InfoBox } from '~/components/InfoBox'

import {
  Tab,
  Tabs,
  TabList,
  TabListWrap,
  TabPanel,
  TabListDecorationWrap,
} from '~/components/Tabs'

import { ProductForm, Wrapper } from './styles'
import * as StyleIcons from '~/icons/styles'

export default ({ products }) => {
  const { sections: SECTIONS, groups: GROUPS } = useContext(ConstantsContext)
  const { styles } = useContext(DesignContext)

  const groupsVisibilityObject = {}
  const [groupsOpen, setGroupOpen] = useState(groupsVisibilityObject)

  const {
    addVariantToCart,
    handleProductOptionChange,
    resetOptions,
    store: { client, adding, selectedProductOptions },
  } = useContext(StoreContext)

  const { node: product } =
    products.find(({ node }) => node.title === selectedProductOptions.style) ||
    products[0]

  const {
    options,
    variants,
    variants: [initialVariant],
    priceRange: { minVariantPrice },
  } = product

  const [variant, setVariant] = useState({ ...initialVariant })

  const findSetVariant = useCallback(
    currentOptions => {
      const selectedVariant = find(variants, ({ selectedOptions }) =>
        isEqual(currentOptions, selectedOptions)
      )

      setVariant({ ...selectedVariant })
    },
    [variants]
  )

  useEffect(() => {
    const { character, style } = selectedProductOptions

    if (styles[style].characters.find(charObj => charObj.value === character))
      return

    handleProductOptionChange('character', styles[style].characters[0].value)
  }, [product])

  useEffect(() => {
    const { size, frame } = selectedProductOptions

    const currentOptions = [
      {
        name: 'size',
        value: size,
      },
      {
        name: 'frame',
        value: frame,
      },
    ]

    findSetVariant(currentOptions)
  }, [product, selectedProductOptions, findSetVariant])

  const productVariant =
    client.product.helpers.variantForOptions(product, variant) || variant

  const handleOptionChange = (optionIndex, value) => {
    const currentOptions = [...variant.selectedOptions]

    currentOptions[optionIndex] = {
      ...currentOptions[optionIndex],
      value,
    }

    if (value === '100x150cm') {
      currentOptions[1] = {
        ...currentOptions[1],
        ...{ value: 'Uden ramme' },
      }
    }

    findSetVariant(currentOptions)
  }

  const price = Intl.NumberFormat(undefined, {
    currency: minVariantPrice.currencyCode,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(variant.price)

  const SizeSection = {
    label: SECTIONS[options[0].name],
    content: {
      id: options[0].id,
      name: options[0].name,
      options: options[0].values.map(value => {
        return {
          value,
          label: value,
          price: `${variants
            .find(({ title }) => title === `${value} / Uden ramme`)
            .price.replace('.00', '')} kr.`,
        }
      }),
    },
  }

  const FrameSection = {
    label: SECTIONS[options[1].name],
    content: {
      id: options[1].id,
      name: options[1].name,
      options: options[1].values.map(value => {
        const framePrice = variants.find(
          ({ title }) => title === `${selectedProductOptions.size} / ${value}`
        )

        const originalPrice = variants.find(
          ({ title }) => title === `${selectedProductOptions.size} / Uden ramme`
        )

        const diffPrice =
          framePrice && originalPrice
            ? (framePrice.price - originalPrice.price).toString()
            : null

        return {
          value,
          label: value,
          price: diffPrice && `${diffPrice.replace('.00', '')} kr.`,
          disabled: !diffPrice,
        }
      }),
    },
  }

  const currentColors = styles[selectedProductOptions.style].colors.find(
    ({ value }) => value === selectedProductOptions.color
  )

  const Groups = [
    {
      label: GROUPS['character'],
      name: 'character',
      sections: [
        {
          label: SECTIONS['character'],
          content: {
            name: 'character',
            options: styles[selectedProductOptions.style].characters,
          },
        },
      ],
    },
    {
      label: GROUPS['style-layout'],
      name: 'style-layout',
      sections: [
        {
          label: SECTIONS['style'],
          content: {
            name: 'style',
            options: products.map(({ node: { title } }, index) => ({
              value: title,
              icon: (() => {
                const Icon = StyleIcons[title]
                return <Icon />
              })(),
              labelStyle: {
                fill:
                  currentColors.colors[selectedProductOptions.inverted ? 1 : 0],
                background:
                  currentColors.colors[selectedProductOptions.inverted ? 0 : 1],
              },
            })),
          },
        },
      ],
    },
    {
      label: GROUPS['color-size'],
      name: 'color-size',
      sections: [
        {
          label: SECTIONS['color'],
          content: {
            name: 'color',
            options: styles[selectedProductOptions.style].colors,
          },
        },
        {
          label: SECTIONS['invert'],
          content: {
            name: 'inverted',
          },
        },
        {
          label: SECTIONS['scale'],
          content: {
            name: 'scale',
          },
        },
      ],
    },
    {
      label: GROUPS['personalised-text'],
      name: 'personalised-text',
      sections: [
        {
          label: SECTIONS['personalisedText'],
          content: {
            name: 'personalisedText',
            placeholder: 'Navn',
          },
        },
        {
          label: SECTIONS['personalisedText2'],
          content: {
            name: 'personalisedText2',
            placeholder: 'Undertekst (F.eks Født 11 December 2019)',
          },
        },
      ],
    },
    {
      label: GROUPS['poster-finish'],
      name: 'poster-finish',
      sections: [SizeSection],
    },
    {
      label: GROUPS['frame'],
      name: 'frame',
      sections: [FrameSection],
    },
  ]

  Groups.map(
    (group, index) => (groupsVisibilityObject[group.name] = index === 0)
  )

  const isDesktop = useMediaQuery(mediaqueries.md)

  return (
    <ProductForm>
      <SEO
        title={product.title}
        keywords={[
          'plakater',
          'plakater kunst',
          'plakater til stuen',
          'plakater til børneværelse',
          'quality poster',
          'poster shop',
          'unique poster',
          'large print',
          'art print',
          'poster copenhagen',
          'quality poster',
        ]}
      />
      <Wrapper>
        <ConditionalWrapper
          condition={!isDesktop}
          wrapper={children => <Tabs>{children}</Tabs>}
        >
          {!isDesktop ? (
            <>
              <TabListDecorationWrap>
                <TabListWrap>
                  <TabList>
                    {Object.keys(GROUPS).map(key => {
                      const label = GROUPS[key]

                      return (
                        <Tab key={nanoid()}>
                          <GreatPrimer>{label}</GreatPrimer>
                        </Tab>
                      )
                    })}
                  </TabList>
                </TabListWrap>
              </TabListDecorationWrap>
              {Groups.map((group, index) => (
                <TabPanelContainer key={index}>
                  <TabPanel>
                    {group.sections.map(({ label, content }, sectionIndex) => {
                      if (
                        label === SECTIONS['personalisedText'] ||
                        label === SECTIONS['personalisedText2']
                      ) {
                        return (
                          <PersonalisedText
                            name={content.name}
                            placeholder={content.placeholder}
                            id={nanoid()}
                            value={selectedProductOptions[content.name]}
                            help={label === SECTIONS['personalisedText2']}
                            onChange={(name, value) =>
                              handleProductOptionChange(name, value)
                            }
                            key={sectionIndex}
                          />
                        )
                      }

                      if (label === SECTIONS['scale']) {
                        return (
                          <Scale
                            name={content.name}
                            id={nanoid()}
                            value={selectedProductOptions[content.name]}
                            onChange={(name, value) =>
                              handleProductOptionChange(name, value)
                            }
                            key={sectionIndex}
                          />
                        )
                      }

                      if (label === SECTIONS['invert']) {
                        return (
                          <Invert
                            name={content.name}
                            id={nanoid()}
                            value={selectedProductOptions[content.name]}
                            onChange={(name, value) =>
                              handleProductOptionChange(name, value)
                            }
                            label={SECTIONS['invert']}
                            key={sectionIndex}
                          />
                        )
                      }

                      return (
                        <Fragment key={sectionIndex}>
                          <Picker
                            {...content}
                            isDesktop={isDesktop}
                            onChange={(name, value) => {
                              handleProductOptionChange(name, value)
                              handleOptionChange(index, value)
                            }}
                            selected={selectedProductOptions[content.name]}
                          />
                        </Fragment>
                      )
                    })}
                  </TabPanel>
                </TabPanelContainer>
              ))}
            </>
          ) : (
            <>
              {Groups.map((group, index) => (
                <Collapsable
                  key={index}
                  label={group.label}
                  name={group.name}
                  open={groupsOpen[group.name]}
                  onClick={setGroupOpen}
                >
                  {group.sections.map(({ label, content }, sectionIndex) => {
                    if (
                      label === SECTIONS['personalisedText'] ||
                      label === SECTIONS['personalisedText2']
                    ) {
                      return (
                        <PersonalisedText
                          name={content.name}
                          id={nanoid()}
                          help={label === SECTIONS['personalisedText2']}
                          placeholder={content.placeholder}
                          value={selectedProductOptions[content.name]}
                          onChange={(name, value) =>
                            handleProductOptionChange(name, value)
                          }
                          key={sectionIndex}
                        />
                      )
                    }

                    if (label === SECTIONS['scale']) {
                      return (
                        <Scale
                          name={content.name}
                          id={nanoid()}
                          value={parseInt(selectedProductOptions[content.name])}
                          onChange={(name, value) =>
                            handleProductOptionChange(name, value)
                          }
                          key={sectionIndex}
                        />
                      )
                    }

                    if (label === SECTIONS['invert']) {
                      return (
                        <Invert
                          name={content.name}
                          id={nanoid()}
                          value={selectedProductOptions[content.name]}
                          onChange={(name, value) =>
                            handleProductOptionChange(name, value)
                          }
                          label={SECTIONS['invert']}
                          key={sectionIndex}
                        />
                      )
                    }

                    return (
                      <Fragment key={sectionIndex}>
                        <Picker
                          {...content}
                          onChange={(name, value) => {
                            handleProductOptionChange(name, value)
                            handleOptionChange(index, value)
                          }}
                          selected={selectedProductOptions[content.name]}
                        />
                      </Fragment>
                    )
                  })}
                </Collapsable>
              ))}
            </>
          )}
        </ConditionalWrapper>
      </Wrapper>
      <InfoBox />

      <AddToCart
        disabled={adding}
        onClick={() => {
          addVariantToCart(productVariant.shopifyId, selectedProductOptions)
          resetOptions()
          // eslint-disable-next-line
          fbTrack('track', 'AddToCart', {
            currency: 'DKK',
            value: productVariant.price,
          })
          navigate('/cart')
        }}
        price={`${price.replace('.00', '')}kr.`}
      />
    </ProductForm>
  )
}
